// Define the structure for new notification data (mirroring backend's GeneralNotificationPayload)
export interface NewNotificationData { // Added export
    notification_id: string;
    user_id_receiver: string;
    notification_type: string;
    actor_user_id?: string;
    actor_username?: string | null;
    actor_avatar_key?: string | null;
    reference_item_id?: string;
    reference_item_type?: string;
    message_summary: string;
    link_target_url?: string;
    is_read: boolean; // Will likely be false from WS
    created_at: number;
    // Any other fields from GeneralNotificationPayload
}

// Define the expected structure of messages from the server
interface WebSocketMessage {
    type: string;
    message?: string;
    data?: NewMessageData | NewNotificationData | TypingUserData | PresenceUpdateData | CallEventData | QueueMatchData | QueueStatusData | ResonanceInviteData;
    conversationId?: string;
    status?: string;
}

// Define structure for attachments, mirroring frontend and backend payloads
interface MessageAttachmentData {
    storageKey: string;
    fileName: string;
    fileType: string;
    fileSize: number;
}

// Define the structure for new message data specifically
interface NewMessageData {
    conversationId: string;
    messageId: string;
    senderUserId: string;
    senderUsername?: string;
    senderAvatarKey?: string | null; // Added to match payload from backend broadcast
    contentText: string;
    messageType: string;
    sentAt: number;
    attachment?: MessageAttachmentData | null; // Added for file attachments
}

interface TypingUserData {
    conversationId: string;
    userId: string;
    username: string;
}

interface PresenceUpdateData {
    channelName: string;
    userId: string;
    username: string;
    avatarUrl?: string;
    isOnline: boolean;
    inCall: boolean;
}

interface QueueMatchData {
    sessionId: string;
    matchedUserId: string;
    matchedUsername?: string;
    projectId?: string;
    conversationId?: string;
}

interface QueueStatusData {
    status: 'idle' | 'searching' | 'matched';
    sessionId?: string;
}

interface ResonanceInviteData {
    id: string;
    fromUserId: string;
    fromUsername?: string;
    targetEchoId: string;
    status: string;
}

// -------- Call signalling types
export interface CallEventData {
    conversationId: string;
    callId: string;
    channelName: string; // Agora channel to join
    fromUserId: string;  // Sender of the signalling message
    fromUsername?: string;
    targetUserId?: string; // Optional – only present on call_invite from caller
    callerUserId?: string; // Optional – callee includes to indicate who initiated the call
    agoraUid?: string; // Agora UID when user joins a call
}

type MessageCallback = (message: NewMessageData) => void;
type AuthStatusCallback = (isAuthenticated: boolean, error?: string) => void;
type UserTypingCallback = (data: TypingUserData) => void;
type NotificationCallback = (notification: NewNotificationData) => void;
type CallEventCallback = (payload: CallEventData) => void;
type PresenceCallback = (payload: { channelName: string; userId: string; username: string; avatarUrl?: string; isOnline: boolean; inCall: boolean }) => void;


// ---------------------------------------------------------------------
// Compute the WebSocket URL.
// Priority: explicit env variable (ws://localhost:8443 / https://api.syncreos.com:8443). Otherwise:
//  * In development -> same hostname at port 8443
//  * In production  -> same hostname and protocol but `wss:`
// ---------------------------------------------------------------------

let computedWsUrl: string | undefined = import.meta.env.PROD ? "https://api.syncreos.com:8443" : "http://localhost:8443";

if (!computedWsUrl || computedWsUrl.trim() === '') {
  if (import.meta.env.DEV) {
    // Use current hostname with :8443 (backend dev server)
    const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    computedWsUrl = `ws://${host}:8443`;
  } else {
    // Production fallback: same host, secure WebSocket
    const loc = typeof window !== 'undefined' ? window.location : undefined;
    if (loc) {
      const scheme = loc.protocol === 'https:' ? 'wss://' : 'ws://';
      computedWsUrl = `${scheme}${loc.host}`;
    }
  }
}

const WEBSOCKET_URL: string = computedWsUrl || 'ws://localhost:8443';

// Import auth store for getting current user ID (safe even outside React components)
// Note: This import is only used in development mode for dev user ID
// import useAuthStore from '@/store/useAuthStore';

class WebSocketService {
    private ws: WebSocket | null = null;
    private messageListeners: MessageCallback[] = [];
    private authStatusListeners: AuthStatusCallback[] = [];
    private userTypingListeners: UserTypingCallback[] = [];
    private userStoppedTypingListeners: UserTypingCallback[] = []; // Same data structure for stop
    private notificationListeners: NotificationCallback[] = [];
    private callInviteListeners: CallEventCallback[] = [];
    private callAcceptListeners: CallEventCallback[] = [];
    private callEndListeners: CallEventCallback[] = [];
    private callJoinListeners: CallEventCallback[] = [];
    private presenceUpdateListeners: PresenceCallback[] = [];
    private queueMatchListeners: ((payload: { sessionId: string; matchedUserId: string; matchedUsername?: string; projectId?: string; conversationId?: string }) => void)[] = [];
    private queueStatusListeners: ((status: 'idle' | 'searching' | 'matched', sessionId?: string) => void)[] = [];
    private isAuthenticated = false;
    // Resonance
    private resonanceInviteListeners: ((invite: { id: string; fromUserId: string; fromUsername?: string; targetEchoId: string; status: string }) => void)[] = [];
    private pendingSubscriptions: string[] = []; // Store conversationIds to subscribe to after auth

    // Ping/Pong and Reconnection properties
    private pingIntervalId: ReturnType<typeof setInterval> | null = null;
    private pongTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private reconnectAttempts = 0;
    private readonly MAX_RECONNECT_ATTEMPTS = 5;
    private readonly INITIAL_RECONNECT_DELAY = 1000; // 1 second
    private currentReconnectDelay = this.INITIAL_RECONNECT_DELAY;
    private readonly PING_INTERVAL = 30000; // 30 seconds
    private readonly PONG_TIMEOUT = 10000; // 10 seconds - if no pong received after ping
    private explicitlyClosed = false;


    constructor() {
    }

    public connect(): void {
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            console.log('WebSocketService: Already connected or connecting.');
            return;
        }
        this.explicitlyClosed = false; // Reset explicit close flag on new connect attempt

        console.log(`WebSocketService: Connecting to ${WEBSOCKET_URL}...`);
        this.ws = new WebSocket(WEBSOCKET_URL);

        this.ws.onopen = () => {
            console.log('WebSocketService: Connection opened.');
            this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
            this.currentReconnectDelay = this.INITIAL_RECONNECT_DELAY; // Reset delay
            if (this.reconnectTimeoutId) {
                clearTimeout(this.reconnectTimeoutId);
                this.reconnectTimeoutId = null;
            }
            this.startPing();
            console.log("WebSocketService: Sending authentication message (HttpOnly cookie).");
            const authMsg: Record<string, unknown> = { type: 'auth' };

            // In local development the JWT cookie isn't available over ws://, so pass userId explicitly.
            // Note: Auth store integration removed to avoid import issues - dev user ID will not be set

            this.ws?.send(JSON.stringify(authMsg));
        };

        this.ws.onmessage = (event) => {
            try {
                const parsedMessage: WebSocketMessage = JSON.parse(event.data as string);
                // console.log('WebSocketService: Received message:', parsedMessage); // Can be too verbose

                switch (parsedMessage.type) {
                    case 'auth_success':
                        this.isAuthenticated = true;
                        console.log('WebSocketService: Authentication successful.');
                        this.notifyAuthStatus(true);
                        this.processPendingSubscriptions();
                        break;
                    case 'auth_failure':
                        this.isAuthenticated = false;
                        console.error('WebSocketService: Authentication failed:', parsedMessage.message);
                        this.notifyAuthStatus(false, parsedMessage.message || 'Authentication failed');
                        this.explicitlyClosed = true; // Prevent reconnection on auth failure
                        this.ws?.close();
                        break;
                    case 'new_message':
                        if (parsedMessage.data) {
                            this.messageListeners.forEach(callback => callback(parsedMessage.data as NewMessageData));
                        }
                        break;
                    case 'new_notification': // Handle new notifications from server
                        if (parsedMessage.data) {
                            this.notificationListeners.forEach(callback => callback(parsedMessage.data as NewNotificationData));
                        }
                        break;
                    case 'subscribe_ack':
                        console.log(`WebSocketService: Subscribed to ${parsedMessage.conversationId} - ${parsedMessage.status}`);
                        break;
                    case 'unsubscribe_ack':
                        console.log(`WebSocketService: Unsubscribed from ${parsedMessage.conversationId} - ${parsedMessage.status}`);
                        break;
                    case 'user_typing': // Server broadcasts this
                        if (parsedMessage.data) {
                            this.userTypingListeners.forEach(callback => callback(parsedMessage.data as TypingUserData));
                        }
                        break;
                    case 'user_stopped_typing': // Server broadcasts this
                        if (parsedMessage.data) {
                            this.userStoppedTypingListeners.forEach(callback => callback(parsedMessage.data as TypingUserData));
                        }
                        break;
                    case 'pong': // Handle pong from server
                        // console.log('WebSocketService: Pong received.');
                        this.handlePong();
                        break;
                    case 'queue_match_found': {
                        console.log('WebSocketService: Queue match found:', parsedMessage);
                        const queueMatchData = parsedMessage.data as QueueMatchData;
                        this.queueMatchListeners.forEach(callback => callback(queueMatchData));
                        break;
                    }
                    case 'queue_status_update': {
                        console.log('WebSocketService: Queue status update:', parsedMessage);
                        const queueStatusData = parsedMessage.data as QueueStatusData;
                        this.queueStatusListeners.forEach(callback => callback(
                            queueStatusData.status,
                            queueStatusData.sessionId
                        ));
                        break;
                    }
                    case 'queue_subscribe_ack':
                        console.log('WebSocketService: Queue subscription acknowledged');
                        break;
                    case 'resonance_invite': {
                        const invite = parsedMessage.data as ResonanceInviteData;
                        this.resonanceInviteListeners.forEach(cb => cb(invite));
                        break;
                    }
                    case 'error':
                        console.error('WebSocketService: Server error message:', parsedMessage.message);
                        // Potentially notify UI or specific error listeners
                        break;
                    case 'call_invite': {
                        const payload = (parsedMessage.data ?? parsedMessage) as unknown as CallEventData;
                        this.callInviteListeners.forEach(cb => cb(payload));
                        break;
                    }
                    case 'call_accept': {
                        const payload = (parsedMessage.data ?? parsedMessage) as unknown as CallEventData;
                        this.callAcceptListeners.forEach(cb => cb(payload));
                        break;
                    }
                    case 'call_end': {
                        const payload = (parsedMessage.data ?? parsedMessage) as unknown as CallEventData;
                        this.callEndListeners.forEach(cb => cb(payload));
                        break;
                    }
                    case 'call_join': {
                        const payload = (parsedMessage.data ?? parsedMessage) as unknown as CallEventData;
                        this.callJoinListeners.forEach(cb => cb(payload));
                        break;
                    }
                    case 'presence_update': {
                        const payload = parsedMessage.data as PresenceUpdateData;
                        this.presenceUpdateListeners.forEach(cb => cb(payload));
                        break;
                    }
                    default:
                        console.log('WebSocketService: Received unhandled message type:', parsedMessage.type);
                }
            } catch (error) {
                console.error('WebSocketService: Error parsing message or in onmessage handler:', error);
            }
        };

        this.ws.onerror = (error) => {
            console.error('WebSocketService: Error:', error);
            // this.isAuthenticated = false; // onclose will handle this
            // this.notifyAuthStatus(false, 'WebSocket connection error.'); // onclose will handle this
            // Note: onerror is usually followed by onclose. Let onclose handle state changes and reconnection.
        };

        this.ws.onclose = (event) => {
            console.log('WebSocketService: Connection closed.', event.reason, `Code: ${event.code}`);
            this.clearPingPongTimeouts();
            this.isAuthenticated = false;
            this.ws = null;
            this.notifyAuthStatus(false, 'Connection closed.');

            if (!this.explicitlyClosed) {
                this.scheduleReconnect();
            }
        };
    }

    private clearPingPongTimeouts(): void {
        if (this.pingIntervalId) {
            clearInterval(this.pingIntervalId);
            this.pingIntervalId = null;
        }
        if (this.pongTimeoutId) {
            clearTimeout(this.pongTimeoutId);
            this.pongTimeoutId = null;
        }
    }

    private startPing(): void {
        this.clearPingPongTimeouts(); // Clear any existing timers
        this.pingIntervalId = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                // console.log('WebSocketService: Sending ping.');
                this.ws.send(JSON.stringify({ type: 'ping' }));
                this.pongTimeoutId = setTimeout(() => {
                    console.warn('WebSocketService: Pong not received in time. Closing connection to trigger reconnect.');
                    this.ws?.close(); // This will trigger onclose, which handles reconnection
                }, this.PONG_TIMEOUT);
            } else {
                // console.log('WebSocketService: WebSocket not open, skipping ping.');
                // If not open, onclose should eventually trigger and handle reconnection.
            }
        }, this.PING_INTERVAL);
    }

    private handlePong(): void {
        if (this.pongTimeoutId) {
            clearTimeout(this.pongTimeoutId);
            this.pongTimeoutId = null;
        }
    }

    private scheduleReconnect(): void {
        if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
            this.reconnectAttempts++;
            console.log(`WebSocketService: Attempting to reconnect... Attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS} in ${this.currentReconnectDelay / 1000}s`);
            
            if (this.reconnectTimeoutId) clearTimeout(this.reconnectTimeoutId); // Clear existing reconnect timeout if any

            this.reconnectTimeoutId = setTimeout(() => {
                this.connect();
            }, this.currentReconnectDelay);
            // Exponential backoff, but cap at a reasonable max (e.g., 30 seconds)
            this.currentReconnectDelay = Math.min(this.currentReconnectDelay * 2, 30000);
        } else {
            console.error('WebSocketService: Max reconnect attempts reached. Giving up.');
            // Optionally reset attempts and delay if you want a manual connect() to start fresh
            this.reconnectAttempts = 0;
            this.currentReconnectDelay = this.INITIAL_RECONNECT_DELAY;
        }
    }


    private notifyAuthStatus(isAuthenticated: boolean, error?: string): void {
        this.authStatusListeners.forEach(callback => callback(isAuthenticated, error));
    }
    
    private processPendingSubscriptions(): void {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.pendingSubscriptions.forEach(conversationId => {
                this.subscribeToConversation(conversationId, false); // false to not re-add to pending
            });
            this.pendingSubscriptions = [];
        }
    }

    public disconnect(): void {
        console.log('WebSocketService: Manual disconnect initiated.');
        this.explicitlyClosed = true;
        this.clearPingPongTimeouts();

        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
        this.reconnectAttempts = 0; // Reset for future manual connect()
        this.currentReconnectDelay = this.INITIAL_RECONNECT_DELAY;

        if (this.ws) {
            this.ws.close();
        }
        // State changes (isAuthenticated, ws=null) will be handled by onclose
    }

    public subscribeToConversation(conversationId: string, addToPendingIfNotAuth = true): void {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log(`WebSocketService: Subscribing to conversation ${conversationId}`);
            this.ws.send(JSON.stringify({ type: 'subscribe', conversationId }));
        } else {
            console.warn(`WebSocketService: Not authenticated or connected. Queuing subscription for ${conversationId}.`);
            if (addToPendingIfNotAuth && !this.pendingSubscriptions.includes(conversationId)) {
                this.pendingSubscriptions.push(conversationId);
            }
            // Optionally, trigger connect if not already trying
            if (!this.ws || (this.ws.readyState !== WebSocket.OPEN && this.ws.readyState !== WebSocket.CONNECTING)) {
                this.connect();
            }
        }
    }

    public unsubscribeFromConversation(conversationId: string): void {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log(`WebSocketService: Unsubscribing from conversation ${conversationId}`);
            this.ws.send(JSON.stringify({ type: 'unsubscribe', conversationId }));
        } else {
            // Remove from pending if it was there
            this.pendingSubscriptions = this.pendingSubscriptions.filter(id => id !== conversationId);
            console.warn(`WebSocketService: Not authenticated or connected. Cannot unsubscribe from ${conversationId}.`);
        }
    }

    public sendTypingIndicator(conversationId: string, isTyping: boolean): void {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const type = isTyping ? 'typing_start' : 'typing_stop';
            console.log(`WebSocketService: Sending ${type} for conversation ${conversationId}`);
            this.ws.send(JSON.stringify({ type, conversationId }));
        } else {
            // console.warn(`WebSocketService: Cannot send typing indicator. Not connected/authenticated.`);
        }
    }

    public onNewMessage(callback: MessageCallback): () => void {
        this.messageListeners.push(callback);
        return () => { // Return an unsubscribe function
            this.messageListeners = this.messageListeners.filter(cb => cb !== callback);
        };
    }

    public onAuthStatusChange(callback: AuthStatusCallback): () => void {
        this.authStatusListeners.push(callback);
        // Immediately notify with current status
        callback(this.isAuthenticated, undefined);
        return () => {
            this.authStatusListeners = this.authStatusListeners.filter(cb => cb !== callback);
        };
    }

    public onUserTyping(callback: UserTypingCallback): () => void {
        this.userTypingListeners.push(callback);
        return () => {
            this.userTypingListeners = this.userTypingListeners.filter(cb => cb !== callback);
        };
    }

    public onUserStoppedTyping(callback: UserTypingCallback): () => void {
        this.userStoppedTypingListeners.push(callback);
        return () => {
            this.userStoppedTypingListeners = this.userStoppedTypingListeners.filter(cb => cb !== callback);
        };
    }

    public onNewNotification(callback: NotificationCallback): () => void {
        this.notificationListeners.push(callback);
        return () => {
            this.notificationListeners = this.notificationListeners.filter(cb => cb !== callback);
        };
    }

    public onPresenceUpdate(callback: PresenceCallback): () => void {
        this.presenceUpdateListeners.push(callback);
        return () => {
            this.presenceUpdateListeners = this.presenceUpdateListeners.filter(cb => cb !== callback);
        };
    }

    public getIsAuthenticated(): boolean {
        return this.isAuthenticated && this.ws?.readyState === WebSocket.OPEN;
    }

    // -------- Call signalling helpers --------

    private emitCallEvent(eventType: 'call_invite' | 'call_accept' | 'call_end' | 'call_join', payload: CallEventData) {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: eventType, data: payload }));
        } else {
            console.warn('WebSocket not ready, cannot send call event');
        }
    }

    public sendCallInvite(payload: CallEventData) {
        this.emitCallEvent('call_invite', payload);
    }

    public sendCallAccept(payload: CallEventData) {
        this.emitCallEvent('call_accept', payload);
    }

    public sendCallEnd(payload: CallEventData) {
        this.emitCallEvent('call_end', payload);
    }

    public sendPresenceUpdate(payload: { channelName: string; userId: string; username: string; avatarUrl?: string; isOnline: boolean; inCall: boolean }) {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'presence_update', data: payload }));
        } else {
            console.warn('WebSocket not ready, cannot send presence update');
        }
    }

    public subscribeToPresenceChannel(channelName: string) {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'subscribe_presence', data: { channelName } }));
        } else {
            console.warn('WebSocket not ready, cannot subscribe to presence channel');
        }
    }

    public unsubscribeFromPresenceChannel(channelName: string) {
        if (this.isAuthenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'unsubscribe_presence', data: { channelName } }));
        } else {
            console.warn('WebSocket not ready, cannot unsubscribe from presence channel');
        }
    }

    public sendCallJoin(payload: CallEventData) {
        this.emitCallEvent('call_join', payload);
    }

    public onCallInvite(cb: CallEventCallback): () => void {
        this.callInviteListeners.push(cb);
        return () => { this.callInviteListeners = this.callInviteListeners.filter(f => f !== cb); };
    }
    public onCallAccept(cb: CallEventCallback): () => void {
        this.callAcceptListeners.push(cb);
        return () => { this.callAcceptListeners = this.callAcceptListeners.filter(f => f !== cb); };
    }
    public onCallEnd(cb: CallEventCallback): () => void {
        this.callEndListeners.push(cb);
        return () => { this.callEndListeners = this.callEndListeners.filter(f => f !== cb); };
    }

    public onCallJoin(cb: CallEventCallback): () => void {
        this.callJoinListeners.push(cb);
        return () => { this.callJoinListeners = this.callJoinListeners.filter(f => f !== cb); };
    }

    // -------- Queue event listeners --------

    public onQueueMatch(cb: (payload: { sessionId: string; matchedUserId: string; matchedUsername?: string; projectId?: string; conversationId?: string }) => void): () => void {
        this.queueMatchListeners.push(cb);
        return () => { this.queueMatchListeners = this.queueMatchListeners.filter(f => f !== cb); };
    }

    public onQueueStatus(cb: (status: 'idle' | 'searching' | 'matched', sessionId?: string) => void): () => void {
        this.queueStatusListeners.push(cb);
        return () => { this.queueStatusListeners = this.queueStatusListeners.filter(f => f !== cb); };
    }

    // Subscribe to queue events
    public subscribeToQueue(): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isAuthenticated) {
            this.ws.send(JSON.stringify({ type: 'queue_subscribe' }));
        } else {
            console.warn('WebSocketService: Cannot subscribe to queue - not connected or authenticated');
        }
    }

    // Resonance helpers
    public onResonanceInvite(cb: (invite: { id: string; fromUserId: string; fromUsername?: string; targetEchoId: string; status: string }) => void): () => void {
        this.resonanceInviteListeners.push(cb);
        return () => { this.resonanceInviteListeners = this.resonanceInviteListeners.filter(f => f !== cb); };
    }
}

// Export a singleton instance
export const websocketService = new WebSocketService();